
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { ContactsUiClient, Contact, Inventory, InventoryContactMapping, InventoryUiClient } from '../../../../backend/backendapi';
import { handleError, initClient } from '../../../../../api';
import CenterOnParent from '../../../../util/CenterOnParent';
import Spinner from '../../../../util/Spinner';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { paginationOptions, searchOptions } from '../../../../../util/vue-good-table';
import ContactSelectModal from '../../../../revobase/contacts/ContactSelectModal';
import { emptyUUID } from '../../../../../util';

@Component({
  components: {
    ContactSelectModal,
    CenterOnParent,
    Spinner,
    VueGoodTable,
  },
})
export default class TabInventoryContacts extends Vue {
  @Prop({ required: true })
  readonly inventory!: Inventory | null;

  readonly api = initClient(InventoryUiClient);
  readonly contactsApi = initClient(ContactsUiClient);
  readonly contacts: Contact[] = [];
  loading = false;

  get availableContacts(): Contact[] {
    const usedContactIds = new Set(this.inventory?.contacts?.map(e => e.contactId));
    return this.contacts.filter(e => !usedContactIds.has(e.id));
  }

  get columnsConfig(): any[] {
    return [
      {
        label: this.$t('common.contacts.model.firstName').toString(),
        field: 'contact.firstName',
      },
      {
        label: this.$t('common.contacts.model.lastName').toString(),
        field: 'contact.lastName',
      },
      {
        label: this.$t('common.contacts.model.salutation').toString(),
        field: 'contact.salutation',
      },
      {
        label: this.$t('common.contacts.model.company').toString(),
        field: 'contact.company',
      },
      {
        label: this.$t('common.contacts.model.email').toString(),
        field: 'contact.email',
      },
      {
        label: this.$t('common.contacts.model.tel').toString(),
        field: 'contact.tel',
      },
      {
        label: this.$t('general.actions').toString(),
        field: 'actions',
      },
    ];
  }

  get contactsById(): Map<string, Contact> {
    return new Map(this.contacts.map(e => [e.id, e]));
  }

  get contactsForDisplay(): { mapping: InventoryContactMapping, contact: Contact | undefined } [] {
    if (!this.inventory) {
      return [];
    }
    return this.inventory?.contacts?.map(e => ({ mapping: e, contact: this.contactsById.get(e.contactId) })) ?? [];
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  async onContactsAdd(contacts: Contact[]): Promise<void> {
    if (!this.inventory) {
      return;
    }

    this.inventory.contacts ??= [];

    //filter duplicates
    contacts = contacts.filter(contact => !this.inventory!.contacts!.some(present => present.contactId === contact.id));

    //add
    for (let contact of contacts) {
      let mapping = new InventoryContactMapping({
        id: emptyUUID(),
        contactId: contact.id,
        inventoryId: this.inventory!.id!,
        createdAt: new Date(),
        createdBy: emptyUUID(),
      });

      try {
        mapping = await this.api.addContactMapping(mapping);
        this.inventory.contacts.push(mapping);
      } catch (error) {
        handleError(this.$toasted, this.$i18n, error);
      }
    }

    (this.$refs.modal as ContactSelectModal).dismiss();
  }

  async onContactRemove(event: { contact: Contact, mapping: InventoryContactMapping }): Promise<void> {
    if (!this.inventory) {
      return;
    }

    this.inventory.contacts ??= [];

    const index = this.inventory.contacts.findIndex(c => c.id === event.mapping.id);
    if (index === -1) {
      return;
    }

    //remove
    try {
      await this.api.deleteContactMapping(this.inventory.contacts[index].id);
      this.inventory.contacts.splice(index, 1); //TODO may remove wrong element
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    }
  }

  @Watch('inventory.contacts', { deep: true })
  private async onInventoryContactsChanged(): Promise<void> {
    this.contacts.splice(0);

    // load relevant contacts
    this.loading = true;
    try {
      const contacts = await this.contactsApi.list();
      this.contacts.push(...contacts);
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    } finally {
      this.loading = false;
    }
  }
}
